import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { ContratoWithDetails } from '@/types/contrato';

interface ContratoResumoDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contrato: ContratoWithDetails;
}

export default function ContratoResumoDialog({
  open,
  onOpenChange,
  contrato,
}: ContratoResumoDialogProps) {
  const formatarTelefone = (telefone: string | undefined) => {
    if (!telefone) return 'Não informado';
    
    // Remove todos os caracteres não numéricos
    const numeroLimpo = telefone.replace(/\D/g, '');
    
    // Verifica se tem 11 dígitos (celular com DDD)
    if (numeroLimpo.length === 11) {
      return `(${numeroLimpo.slice(0, 2)})${numeroLimpo.slice(2, 7)}-${numeroLimpo.slice(7)}`;
    }
    
    // Verifica se tem 10 dígitos (fixo com DDD)
    if (numeroLimpo.length === 10) {
      return `(${numeroLimpo.slice(0, 2)})${numeroLimpo.slice(2, 6)}-${numeroLimpo.slice(6)}`;
    }
    
    // Se não tem o formato esperado, retorna o original
    return telefone;
  };

  const valorKit = contrato.valor_kit || contrato.proposta?.valor_total_kits || 0;
  const valorIntegracao = contrato.valor_integracao || contrato.proposta?.preco_integracao || 0;
  const valorProjeto = valorKit + valorIntegracao;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle>Revisão Geral do Contrato</DialogTitle>
          <DialogDescription>
            Resumo das informações do contrato
          </DialogDescription>
        </DialogHeader>
        <ScrollArea className="max-h-[calc(90vh-120px)] pr-4">
          <div className="space-y-6">
            {/* Seção 1: Dados do Cliente */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-blue-800 border-b border-blue-200 pb-2">
                📋 Dados do Cliente
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-blue-50 p-4 rounded-lg">
                <div>
                  <Label className="text-sm font-medium text-gray-600">Nome/Razão Social</Label>
                  <p className="text-sm font-semibold">
                    {contrato.lead?.tipo === 'PF' ? contrato.lead?.nome : contrato.lead?.razao_social || contrato.nome_cliente || 'Não informado'}
                  </p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-600">Tipo</Label>
                  <p className="text-sm">
                    {(contrato.lead?.tipo === 'PF' || contrato.lead?.tipo === 'fisica') ? 'Pessoa Física' : 'Pessoa Jurídica'}
                  </p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-600">Telefone</Label>
                  <p className="text-sm">{formatarTelefone(contrato.lead?.telefone)}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-600">Grupo Tarifário</Label>
                  <p className="text-sm">{contrato.dimensionamento?.grupo_tarifario || 'Não informado'}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-600">Endereço Completo</Label>
                  <p className="text-sm">
                    {[
                      contrato.lead?.endereco || contrato.endereco,
                      contrato.lead?.numero || contrato.numero,
                      contrato.lead?.cidade || contrato.cidade,
                      contrato.lead?.estado || contrato.estado,
                      contrato.lead?.cep || contrato.cep
                    ].filter(Boolean).join(', ') || 'Endereço não informado'}
                  </p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-600">Vendedor</Label>
                  <p className="text-sm">{contrato.lead?.vendedor_nome || 'Não informado'}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-600">Parceiro</Label>
                  <p className="text-sm">{contrato.lead?.parceiro_nome || 'Não informado'}</p>
                </div>
                {contrato.lead?.tipo === 'PJ' && contrato.lead?.cnpj && (
                  <div className="md:col-span-2">
                    <Label className="text-sm font-medium text-gray-600">CNPJ</Label>
                    <p className="text-sm">{contrato.lead.cnpj}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Seção 2: Dados Gerais do Sistema */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-green-800 border-b border-green-200 pb-2">
                ⚡ Dados Gerais do Sistema
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-green-50 p-4 rounded-lg">
                <div>
                  <Label className="text-sm font-medium text-gray-600">Potência do Sistema</Label>
                  <p className="text-sm font-semibold">
                    {contrato.proposta?.potencia_total_paineis || contrato.potencia_total_paineis || 0} kWp
                  </p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-600">Número de Placas</Label>
                  <p className="text-sm font-semibold">
                    {contrato.proposta?.total_paineis || contrato.total_paineis || 0} unidades
                  </p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-600">Potência do Inversor</Label>
                  <p className="text-sm font-semibold">
                    {contrato.proposta?.potencia_total_inversores || contrato.potencia_total_inversores || 0} kW
                  </p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-600">Valor do Kit</Label>
                  <p className="text-sm font-semibold">
                    R$ {valorKit.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </p>
                </div>
                {contrato.dimensionamento?.nome_dimensionamento && (
                  <div className="md:col-span-2">
                    <Label className="text-sm font-medium text-gray-600">Dimensionamento</Label>
                    <p className="text-sm font-semibold">{contrato.dimensionamento.nome_dimensionamento}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Seção 3: Dados Financeiros do Projeto */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-purple-800 border-b border-purple-200 pb-2">
                💰 Dados Financeiros do Projeto
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-purple-50 p-4 rounded-lg">
                <div>
                  <Label className="text-sm font-medium text-gray-600">Valor do Projeto</Label>
                  <p className="text-sm font-semibold text-purple-700">
                    R$ {valorProjeto.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-600">Comissão Vendedor</Label>
                  <p className="text-sm">
                    R$ {(contrato.proposta?.comissao_vendedor_valor || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-600">Valor do Kit</Label>
                  <p className="text-sm">
                    R$ {valorKit.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-600">Comissão Parceiro</Label>
                  <p className="text-sm">
                    R$ {(contrato.proposta?.comissao_parceiro_valor || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                </div>
                {contrato.proposta?.comissao_parceiro_pai_valor !== undefined && contrato.proposta.comissao_parceiro_pai_valor > 0 && (
                  <div>
                    <Label className="text-sm font-medium text-gray-600">Comissão Parceiro Pai</Label>
                    <p className="text-sm">
                      R$ {contrato.proposta.comissao_parceiro_pai_valor.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                  </div>
                )}
                <div>
                  <Label className="text-sm font-medium text-gray-600">Valor da Integração</Label>
                  <p className="text-sm">
                    R$ {valorIntegracao.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                </div>
                {contrato.proposta?.mao_obra !== undefined && (
                  <div>
                    <Label className="text-sm font-medium text-gray-600">Valor da Mão de Obra</Label>
                    <p className="text-sm">
                      R$ {(contrato.proposta.mao_obra || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                  </div>
                )}
                {contrato.proposta?.valor_estrutura_metallica !== undefined && (
                  <div>
                    <Label className="text-sm font-medium text-gray-600">Valor da Estrutura Metálica</Label>
                    <p className="text-sm">
                      R$ {(contrato.proposta.valor_estrutura_metallica || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                  </div>
                )}
                {contrato.proposta?.custo_total_integracao !== undefined && (
                  <div>
                    <Label className="text-sm font-medium text-gray-600">Custo Total da Integração</Label>
                    <p className="text-sm">
                      R$ {(contrato.proposta.custo_total_integracao || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                  </div>
                )}
                {contrato.proposta?.lucro_liquido !== undefined && (
                  <div>
                    <Label className="text-sm font-medium text-gray-600">Lucro Líquido Esperado</Label>
                    <p className="text-sm font-semibold text-green-600">
                      R$ {(contrato.proposta.lucro_liquido || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                  </div>
                )}
                {contrato.proposta?.custo_total_implantacao !== undefined && (
                  <div>
                    <Label className="text-sm font-medium text-gray-600">Custo Total de Implantação</Label>
                    <p className="text-sm font-semibold text-orange-600">
                      R$ {(contrato.proposta.custo_total_implantacao || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}

